import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from 'recharts';
import { api } from '../services/api';
import DateRangePicker from '../components/DateRangePicker';
import InfoPanel from '../components/InfoPanel';
import { useDateRange } from '../hooks/useDateRange';

const COLORS = [
  '#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
  '#14b8a6', '#a855f7', '#22c55e', '#eab308', '#64748b',
  '#0ea5e9', '#d946ef', '#f43f5e', '#78716c', '#7c3aed',
];

export default function Dashboard() {
  const { fromDate, toDate, setFromDate, setToDate, from, to } = useDateRange();
  const [selectedBook, setSelectedBook] = useState('');
  const [selectedEdition, setSelectedEdition] = useState('');

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['summary', from, to],
    queryFn: () => api.getSummary(from, to),
  });

  const { data: topAuthors, isLoading: authorsLoading } = useQuery({
    queryKey: ['topAuthors', from, to],
    queryFn: () => api.getTopAuthors(20, from, to),
  });

  const { data: topUsers, isLoading: usersLoading } = useQuery({
    queryKey: ['topUsers', from, to],
    queryFn: () => api.getTopUsers(20, from, to),
  });

  const { data: booksList } = useQuery({
    queryKey: ['booksList'],
    queryFn: () => api.getBooksList(),
  });

  const { data: editionsList } = useQuery({
    queryKey: ['editionsList', selectedBook],
    queryFn: () => api.getEditionsList(selectedBook),
    enabled: !!selectedBook,
  });

  const { data: bookViews } = useQuery({
    queryKey: ['bookViews', selectedBook, selectedEdition, from, to],
    queryFn: () => api.getViewsByBook(selectedBook, selectedEdition, from, to),
    enabled: !!selectedBook,
  });

  const barChartData = useMemo(() => {
    if (!topAuthors?.authors) return [];
    return topAuthors.authors.map((author) => ({
      name: author.authors.length > 35 ? author.authors.substring(0, 35) + '...' : author.authors,
      fullName: author.authors,
      value: Number(author.view_count),
    }));
  }, [topAuthors]);

  return (
    <div className="space-y-6">
      <InfoPanel id="dashboard" title="Welcome to the Dashboard">
        This is your analytics overview. View total content views, top authors, and most active users.
        Use the date picker to filter data by time period. Select a specific book to see its view count.
      </InfoPanel>

      <div className="flex justify-between items-start">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <DateRangePicker
          from={fromDate}
          to={toDate}
          onFromChange={setFromDate}
          onToChange={setToDate}
        />
      </div>

      {/* Summary Card */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-700 mb-2">
          Total Content Views
        </h2>
        {summaryLoading ? (
          <div className="animate-pulse h-10 bg-gray-200 rounded w-32"></div>
        ) : (
          <p className="text-4xl font-bold text-primary-600">
            {summary?.totalViews?.toLocaleString() || 0}
          </p>
        )}
      </div>

      {/* Book Filter */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">
          Report by Book
        </h2>
        <div className="flex items-end space-x-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Book
            </label>
            <select
              value={selectedBook}
              onChange={(e) => {
                setSelectedBook(e.target.value);
                setSelectedEdition('');
              }}
              className="input"
            >
              <option value="">Select a book</option>
              {booksList?.books.map((book) => (
                <option key={book} value={book}>
                  {book}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Edition
            </label>
            <select
              value={selectedEdition}
              onChange={(e) => setSelectedEdition(e.target.value)}
              className="input"
              disabled={!selectedBook}
            >
              <option value="">All editions</option>
              {editionsList?.editions.map((edition) => (
                <option key={edition} value={edition}>
                  {edition}
                </option>
              ))}
            </select>
          </div>
          {selectedBook && bookViews && (
            <div className="px-6 py-3 bg-primary-50 rounded-md">
              <p className="text-sm text-gray-500">Views</p>
              <p className="text-2xl font-bold text-primary-600">
                {bookViews.viewCount.toLocaleString()}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Tables side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Authors */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">
            Top 20 Authors by Total Views
          </h2>
          {authorsLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse h-8 bg-gray-200 rounded"></div>
              ))}
            </div>
          ) : (
            <div className="table-container max-h-96 overflow-y-auto">
              <table className="table w-full">
                <thead className="sticky top-0">
                  <tr>
                    <th className="w-3/4">Author</th>
                    <th className="text-right whitespace-nowrap w-1/4">Views</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {topAuthors?.authors.map((author, index) => (
                    <tr key={index}>
                      <td className="break-words max-w-0">{author.authors}</td>
                      <td className="text-right font-medium whitespace-nowrap">
                        {Number(author.view_count).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Top Users */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">
            Top 20 Users by Usage
          </h2>
          {usersLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse h-8 bg-gray-200 rounded"></div>
              ))}
            </div>
          ) : (
            <div className="table-container max-h-96 overflow-y-auto">
              <table className="table w-full">
                <thead className="sticky top-0">
                  <tr>
                    <th className="w-3/4">Email</th>
                    <th className="text-right whitespace-nowrap w-1/4">Views</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {topUsers?.users.map((user, index) => (
                    <tr key={index}>
                      <td className="break-words max-w-0">{user.email}</td>
                      <td className="text-right font-medium whitespace-nowrap">
                        {Number(user.view_count).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Authors Bar Chart - full width below */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">
          Views by Author
        </h2>
        <div className="h-[600px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={barChartData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <XAxis type="number" tickFormatter={(value) => value.toLocaleString()} />
              <YAxis
                type="category"
                dataKey="name"
                width={350}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value: number) => value.toLocaleString()}
                labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
              />
              <Bar
                dataKey="value"
                radius={[0, 4, 4, 0]}
                label={{ position: 'right', formatter: (value: number) => value.toLocaleString(), fontSize: 12 }}
              >
                {barChartData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
